import { Layers } from 'lucide-react';
import { EmptyState, Button } from '@/components/ui';

interface LayoutUnavailablePanelProps {
  modelId: string;
  modelVersion: string;
  latestVersionWithLayout: string | null;
  /**
   * Triggered when the user clicks the "Start new conversation on `<version>`"
   * CTA. Wired by the parent route (`routes/builder.tsx`) to flip its
   * existing `setShowNewModal(true)` state — the panel cannot reach the
   * modal directly because the modal trigger lives on the route.
   */
  onCreateNew?: () => void;
}

/**
 * Renders inside the Model tab when the active conversation is pinned to a
 * `(modelId, modelVersion)` whose `layout_url` is null — i.e., the model row
 * predates the in-platform-model-view Phase 1 ingest path that emits
 * `layout.json`. Two branches:
 *
 *   - `latestVersionWithLayout` non-null → primary action "Start new
 *     conversation on `<modelId>@<latestVersionWithLayout>`". Clicking the
 *     CTA flips the Builder's `NewConversationModal` open via the
 *     `onCreateNew` callback.
 *   - `latestVersionWithLayout` null → no layout-bearing version exists yet.
 *     CTA disappears; copy switches to a contact-admin fallback. The
 *     Conversation tab stays fully functional regardless.
 *
 * Distinct from `<MissingModelPanel>` — that's HTTP 410 (model version
 * removed); this is HTTP 200 with a capability gap (model version exists
 * but has no layout artifact). Both panels live in the Builder route's
 * Model-tab content area.
 */
export function LayoutUnavailablePanel({
  modelId,
  modelVersion,
  latestVersionWithLayout,
  onCreateNew,
}: LayoutUnavailablePanelProps) {
  return (
    <EmptyState
      icon={<Layers size={24} className="text-text-muted" />}
      title="Model view not available for this version"
      description={
        latestVersionWithLayout
          ? `This conversation is pinned to ${modelId}@${modelVersion}, which predates the in-platform Model tab. Start a new conversation on ${modelId}@${latestVersionWithLayout} to see the spreadsheet view.`
          : `No layout-bearing version of ${modelId} has been ingested yet. Contact your admin to re-ingest the model.`
      }
      action={
        latestVersionWithLayout && onCreateNew ? (
          <Button variant="primary" onClick={onCreateNew}>
            Start new conversation on {latestVersionWithLayout}
          </Button>
        ) : undefined
      }
      className="h-full"
    />
  );
}
